import React, { useState, useEffect } from 'react'
import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonButton,
  IonIcon,
  IonList,
  IonItem,
  IonLabel,
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonInput,
  IonTextarea,
  IonSelect,
  IonSelectOption,
  IonCheckbox,
  IonChip,
  IonBadge,
  IonAlert,
  IonToast,
  IonGrid,
  IonRow,
  IonCol,
  IonNote,

  IonAvatar,
  IonItemSliding,
  IonItemOptions,
  IonItemOption,
  IonSearchbar,
  IonSpinner
} from '@ionic/react'
import {
  peopleOutline,
  addOutline,
  closeOutline,

  pencilOutline,
  trashOutline,
  personAddOutline,
  personRemoveOutline,
  colorPaletteOutline,
  mailOutline,
  chatbubblesOutline,
  personOutline,
  chevronBackOutline,
  chevronForwardOutline,
  playBackOutline,
  playForwardOutline,
  searchOutline,
  settingsOutline,
  listOutline
} from 'ionicons/icons'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../store/authStore'
import { supabase } from '../services/supabase'
import Avatar from './Avatar'

interface ClientGroup {
  id: string
  coach_id: string
  name: string
  description?: string
  color: string
  created_at: string
  updated_at: string
  member_count?: number
}

interface GroupMember {
  id: string
  group_id: string
  coachee_id: string
  added_at: string
  coachee_name: string
  coachee_email: string
  coachee_avatar_url?: string
}

interface Coachee {
  id: string
  email: string
  full_name: string
  avatar_url?: string
}

const GROUP_COLORS = [
  '#3b82f6', // Blue
  '#10b981', // Green
  '#f59e0b', // Yellow
  '#ef4444', // Red
  '#8b5cf6', // Purple
  '#f97316', // Orange
  '#06b6d4', // Cyan
  '#84cc16', // Lime
  '#ec4899', // Pink
  '#6b7280'  // Gray
]

const GroupManagement: React.FC = () => {
  const { user } = useAuthStore()
  const { t } = useTranslation()
  
  const [groups, setGroups] = useState<ClientGroup[]>([])
  const [availableCoachees, setAvailableCoachees] = useState<Coachee[]>([])
  const [selectedGroup, setSelectedGroup] = useState<ClientGroup | null>(null)
  const [groupMembers, setGroupMembers] = useState<GroupMember[]>([])
  
  // Modal states
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false)
  const [showEditGroupModal, setShowEditGroupModal] = useState(false)
  const [showGroupDetailsModal, setShowGroupDetailsModal] = useState(false)
  const [showAddMembersModal, setShowAddMembersModal] = useState(false)
  
  // Form states
  const [groupForm, setGroupForm] = useState({
    name: '',
    description: '',
    color: GROUP_COLORS[0]
  })
  const [selectedCoachees, setSelectedCoachees] = useState<string[]>([])
  
  // UI states
  const [isLoading, setIsLoading] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [showDeleteAlert, setShowDeleteAlert] = useState(false)
  const [groupToDelete, setGroupToDelete] = useState<string | null>(null)

  const [showRemoveMemberAlert, setShowRemoveMemberAlert] = useState(false)
  const [memberToRemove, setMemberToRemove] = useState<GroupMember | null>(null)

  // Search, filter, and pagination state
  const [searchQuery, setSearchQuery] = useState('')
  const [memberCountFilter, setMemberCountFilter] = useState<string>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(5)

  // Group details search and filter state
  const [memberSearchQuery, setMemberSearchQuery] = useState('')
  const [memberCurrentPage, setMemberCurrentPage] = useState(1)
  const memberItemsPerPage = 10

  useEffect(() => {
    if (user) {
      fetchGroups()
      fetchAvailableCoachees()
    }
  }, [user])

  const fetchGroups = async () => {
    try {
      const { data, error } = await supabase
        .from('client_groups')
        .select(`
          *,
          client_group_memberships(count)
        `)
        .eq('coach_id', user!.id)
        .order('created_at', { ascending: false })

      if (error) throw error

      const groupsWithCount = data?.map(group => ({
        ...group,
        member_count: group.client_group_memberships?.[0]?.count || 0
      })) || []

      setGroups(groupsWithCount)
    } catch (error) {
      console.error('Error fetching groups:', error)
    }
  }

  const fetchAvailableCoachees = async () => {
    try {
      const { data, error } = await supabase
        .from('coach_coachee_relationships')
        .select(`
          coachee_id,
          profiles!coach_coachee_relationships_coachee_id_fkey (
            id,
            email,
            full_name,
            avatar_url
          )
        `)
        .eq('coach_id', user!.id)

      if (error) throw error

      const coacheesData = data?.map((rel: any) => ({
        id: rel.profiles.id,
        email: rel.profiles.email,
        full_name: rel.profiles.full_name || '',
        avatar_url: rel.profiles.avatar_url
      })) || []

      setAvailableCoachees(coacheesData)
    } catch (error) {
      console.error('Error fetching coachees:', error)
    }
  }

  const fetchGroupMembers = async (groupId: string) => {
    try {
      const { data, error } = await supabase
        .from('client_group_memberships')
        .select(`
          *,
          profiles!client_group_memberships_coachee_id_fkey (
            id,
            email,
            full_name,
            avatar_url
          )
        `)
        .eq('group_id', groupId)
        .order('added_at', { ascending: false })

      if (error) throw error

      const membersData = data?.map((membership: any) => ({
        id: membership.id,
        group_id: membership.group_id,
        coachee_id: membership.coachee_id,
        added_at: membership.added_at,
        coachee_name: membership.profiles.full_name || '',
        coachee_email: membership.profiles.email,
        coachee_avatar_url: membership.profiles.avatar_url
      })) || []

      setGroupMembers(membersData)
    } catch (error) {
      console.error('Error fetching group members:', error)
    }
  }

  const createGroup = async () => {
    if (!groupForm.name.trim()) return

    setIsLoading(true)
    try {
      console.log('Creating group with user:', user)
      console.log('User role:', user?.role)
      console.log('Group data:', {
        coach_id: user!.id,
        name: groupForm.name.trim(),
        description: groupForm.description.trim() || null,
        color: groupForm.color
      })

      const { data, error } = await supabase
        .from('client_groups')
        .insert([{
          coach_id: user!.id,
          name: groupForm.name.trim(),
          description: groupForm.description.trim() || null,
          color: groupForm.color
        }])
        .select()
        .single()

      if (error) {
        console.error('Supabase error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        })
        throw error
      }

      // Add selected coachees to the group
      if (selectedCoachees.length > 0) {
        const memberships = selectedCoachees.map(coacheeId => ({
          group_id: data.id,
          coachee_id: coacheeId
        }))

        const { error: membershipError } = await supabase
          .from('client_group_memberships')
          .insert(memberships)

        if (membershipError) throw membershipError
      }

      setToastMessage(t('groups.createSuccess', 'Group created successfully'))
      setShowToast(true)
      setShowCreateGroupModal(false)
      resetForm()
      fetchGroups()
    } catch (error) {
      console.error('Error creating group:', error)
      setToastMessage(t('groups.createError', 'Failed to create group'))
      setShowToast(true)
    } finally {
      setIsLoading(false)
    }
  }

  const updateGroup = async () => {
    if (!selectedGroup || !groupForm.name.trim()) return

    setIsLoading(true)
    try {
      const { error } = await supabase
        .from('client_groups')
        .update({
          name: groupForm.name.trim(),
          description: groupForm.description.trim() || null,
          color: groupForm.color
        })
        .eq('id', selectedGroup.id)

      if (error) throw error

      setToastMessage(t('groups.updateSuccess', 'Group updated successfully'))
      setShowToast(true)
      setShowEditGroupModal(false)
      setSelectedGroup(null)
      resetForm()
      fetchGroups()
    } catch (error) {
      console.error('Error updating group:', error)
      setToastMessage(t('groups.updateError', 'Failed to update group'))
      setShowToast(true)
    } finally {
      setIsLoading(false)
    }
  }

  const deleteGroup = async () => {
    if (!groupToDelete) return

    setIsLoading(true)
    try {
      const { error } = await supabase
        .from('client_groups')
        .delete()
        .eq('id', groupToDelete)

      if (error) throw error

      setToastMessage(t('groups.deleteSuccess', 'Group deleted successfully'))
      setShowToast(true)
      setShowDeleteAlert(false)
      setGroupToDelete(null)
      fetchGroups()
    } catch (error) {
      console.error('Error deleting group:', error)
      setToastMessage(t('groups.deleteError', 'Failed to delete group'))
      setShowToast(true)
    } finally {
      setIsLoading(false)
    }
  }

  const addMembersToGroup = async () => {
    if (!selectedGroup || selectedCoachees.length === 0) return

    setIsLoading(true)
    try {
      const memberships = selectedCoachees.map(coacheeId => ({
        group_id: selectedGroup.id,
        coachee_id: coacheeId
      }))

      const { error } = await supabase
        .from('client_group_memberships')
        .insert(memberships)

      if (error) throw error

      setToastMessage(t('groups.membersAddedSuccess', 'Members added successfully'))
      setShowToast(true)
      setShowAddMembersModal(false)
      setSelectedCoachees([])
      fetchGroups()
      if (showGroupDetailsModal) {
        fetchGroupMembers(selectedGroup.id)
      }
    } catch (error) {
      console.error('Error adding members:', error)
      setToastMessage(t('groups.membersAddedError', 'Failed to add members'))
      setShowToast(true)
    } finally {
      setIsLoading(false)
    }
  }

  const confirmRemoveMember = (member: GroupMember) => {
    setMemberToRemove(member)
    setShowRemoveMemberAlert(true)
  }

  const removeMemberFromGroup = async () => {
    if (!memberToRemove) return

    try {
      const { error } = await supabase
        .from('client_group_memberships')
        .delete()
        .eq('id', memberToRemove.id)

      if (error) throw error

      setToastMessage(t('groups.memberRemovedSuccess', 'Member removed successfully'))
      setShowToast(true)
      setShowRemoveMemberAlert(false)
      setMemberToRemove(null)
      fetchGroups()
      if (selectedGroup) {
        fetchGroupMembers(selectedGroup.id)
      }
    } catch (error) {
      console.error('Error removing member:', error)
      setToastMessage(t('groups.memberRemovedError', 'Failed to remove member'))
      setShowToast(true)
    }
  }

  const resetForm = () => {
    setGroupForm({
      name: '',
      description: '',
      color: GROUP_COLORS[0]
    })
    setSelectedCoachees([])
  }

  const openCreateGroupModal = () => {
    resetForm()
    setShowCreateGroupModal(true)
  }

  const openEditGroupModal = (group: ClientGroup) => {
    setSelectedGroup(group)
    setGroupForm({
      name: group.name,
      description: group.description || '',
      color: group.color
    })
    setShowEditGroupModal(true)
  }

  const openGroupDetailsModal = (group: ClientGroup) => {
    setSelectedGroup(group)
    fetchGroupMembers(group.id)
    setShowGroupDetailsModal(true)
  }

  const openAddMembersModal = (group: ClientGroup) => {
    setSelectedGroup(group)
    setSelectedCoachees([])
    setShowAddMembersModal(true)
  }



  const getAvailableCoacheesForGroup = () => {
    if (!selectedGroup) return availableCoachees

    return availableCoachees.filter(coachee => 
      !groupMembers.some(member => member.coachee_id === coachee.id)
    )
  }

  // Filter and pagination logic
  const filteredGroups = groups.filter(group => {
    const matchesSearch = 
      group.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      group.description?.toLowerCase().includes(searchQuery.toLowerCase())

    const matchesMemberCount = 
      memberCountFilter === 'all' ||
      (memberCountFilter === 'empty' && (group.member_count || 0) === 0) ||
      (memberCountFilter === 'small' && (group.member_count || 0) >= 1 && (group.member_count || 0) <= 3) ||
      (memberCountFilter === 'medium' && (group.member_count || 0) >= 4 && (group.member_count || 0) <= 10) ||
      (memberCountFilter === 'large' && (group.member_count || 0) > 10)

    return matchesSearch && matchesMemberCount
  })

  const totalPages = Math.ceil(filteredGroups.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedGroups = filteredGroups.slice(startIndex, startIndex + itemsPerPage)

  const handleSearchChange = (query: string) => {
    setSearchQuery(query)
    setCurrentPage(1) // Reset to first page when searching
  }

  const handleMemberCountFilterChange = (filter: string) => {
    setMemberCountFilter(filter)
    setCurrentPage(1) // Reset to first page when filtering
  }

  // Member filtering and pagination logic
  const filteredMembers = groupMembers.filter(member => {
    const matchesSearch = 
      member.coachee_name?.toLowerCase().includes(memberSearchQuery.toLowerCase()) ||
      member.coachee_email?.toLowerCase().includes(memberSearchQuery.toLowerCase())
    return matchesSearch
  })

  const memberTotalPages = Math.ceil(filteredMembers.length / memberItemsPerPage)
  const memberStartIndex = (memberCurrentPage - 1) * memberItemsPerPage
  const paginatedMembers = filteredMembers.slice(memberStartIndex, memberStartIndex + memberItemsPerPage)

  const handleMemberSearchChange = (query: string) => {
    setMemberSearchQuery(query)
    setMemberCurrentPage(1) // Reset to first page when searching
  }

  const handlePageSizeChange = (newSize: number) => {
    setItemsPerPage(newSize)
    setCurrentPage(1) // Reset to first page when changing page size
  }

  return (
    <>
      <IonCard>
        <IonCardHeader>
          <IonCardTitle className="flex items-center justify-between text-base sm:text-lg">
            <div className="flex items-center">
              <IonIcon icon={peopleOutline} className="mr-2 flex-shrink-0" />
              {t('groups.title', 'Groups')}
            </div>
            <div className="flex items-center space-x-1 flex-shrink-0" style={{marginRight: '-12px'}}>
              <IonButton 
                fill="clear" 
                size="small"
                onClick={openCreateGroupModal}
              >
                <IonIcon icon={peopleOutline} className="mr-1" />
                <IonIcon icon={addOutline} />
                <span className="hidden sm:inline ml-2">{t('groups.createGroup', 'Create Group')}</span>
              </IonButton>
            </div>
          </IonCardTitle>
        </IonCardHeader>
        <IonCardContent className="ion-no-padding">
          {/* Search and Filter Controls */}
          <div className="ion-padding-horizontal ion-padding-top mb-4 space-y-3">
            <IonSearchbar
              value={searchQuery}
              onIonInput={(e: CustomEvent) => handleSearchChange(e.detail.value!)}
              placeholder={t('groups.search', 'Search groups...')}
              debounce={300}
            />
            
            <div className="flex items-center space-x-2">
              <IonSelect
                value={memberCountFilter}
                onIonChange={(e) => handleMemberCountFilterChange(e.detail.value)}
                placeholder={t('groups.filterBySize', 'Filter by size')}
                interface="popover"
                className="flex-1"
              >
                <IonSelectOption value="all">{t('groups.sizeAll', 'All Sizes')}</IonSelectOption>
                <IonSelectOption value="empty">{t('groups.sizeEmpty', 'Empty (0 members)')}</IonSelectOption>
                <IonSelectOption value="small">{t('groups.sizeSmall', 'Small (1-3 members)')}</IonSelectOption>
                <IonSelectOption value="medium">{t('groups.sizeMedium', 'Medium (4-10 members)')}</IonSelectOption>
                <IonSelectOption value="large">{t('groups.sizeLarge', 'Large (10+ members)')}</IonSelectOption>
              </IonSelect>
              
              {(searchQuery || memberCountFilter !== 'all') && (
                <IonButton 
                  fill="clear" 
                  size="small"
                  onClick={() => {
                    setSearchQuery('')
                    setMemberCountFilter('all')
                    setCurrentPage(1)
                  }}
                >
                  <IonIcon icon={closeOutline} />
                </IonButton>
              )}
            </div>
          </div>

          {isLoading ? (
            <div className="text-center py-8 ion-padding-horizontal">
              <IonSpinner />
            </div>
          ) : filteredGroups.length > 0 ? (
            <>
              <IonList>
                {paginatedGroups.map((group) => (
                  <IonItem key={group.id} button onClick={() => openGroupDetailsModal(group)}>
                    <div 
                      className="w-4 h-4 rounded-full mr-3"
                      style={{ backgroundColor: group.color }}
                    />
                    <IonLabel>
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium flex-1">{group.name}</h3>
                        <IonChip color="medium" className="ml-2">
                          <IonIcon icon={peopleOutline} />
                          <IonLabel>{group.member_count}</IonLabel>
                        </IonChip>
                      </div>
                      {group.description && (
                        <p className="text-sm text-gray-600">{group.description}</p>
                      )}
                      <p className="text-xs text-gray-500">
                        Created: {new Date(group.created_at).toLocaleDateString()}
                      </p>
                    </IonLabel>
                  </IonItem>
                ))}
              </IonList>

              {/* Pagination Controls */}
              <div className="flex flex-col space-y-4 mt-4 ion-padding-horizontal">
                {/* Items per page selector and results info */}
                <div className="flex flex-nowrap items-center justify-between text-sm">
                  <div className="flex items-center space-x-1">
                    <span className="text-gray-600">{t('common.show', 'Show')}:</span>
                    <IonSelect
                      value={itemsPerPage}
                      onIonChange={(e) => handlePageSizeChange(e.detail.value)}
                      interface="popover"
                      className="w-16"
                    >
                      <IonSelectOption value={3}>3</IonSelectOption>
                      <IonSelectOption value={5}>5</IonSelectOption>
                      <IonSelectOption value={10}>10</IonSelectOption>
                      <IonSelectOption value={25}>25</IonSelectOption>
                      <IonSelectOption value={50}>50</IonSelectOption>
                    </IonSelect>
                  </div>
                  <div className="text-gray-600">
                    {t('common.showing', 'Showing')} {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredGroups.length)} {t('common.of', 'of')} {filteredGroups.length}
                  </div>
                </div>

                {/* Navigation controls */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between">
                    {/* Desktop navigation - full text */}
                    <div className="hidden sm:flex items-center space-x-2">
                      <IonButton
                        fill="clear"
                        size="small"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(1)}
                        className="min-w-[60px]"
                      >
                        <IonIcon icon={playBackOutline} slot="start" />
                        {t('common.first', 'First')}
                      </IonButton>
                      <IonButton
                        fill="clear"
                        size="small"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(currentPage - 1)}
                        className="min-w-[80px]"
                      >
                        <IonIcon icon={chevronBackOutline} slot="start" />
                        {t('common.previous', 'Previous')}
                      </IonButton>
                    </div>

                    {/* Mobile navigation - icons only */}
                    <div className="flex sm:hidden items-center space-x-1">
                      <IonButton
                        fill="clear"
                        size="small"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(1)}
                      >
                        <IonIcon icon={playBackOutline} />
                      </IonButton>
                      <IonButton
                        fill="clear"
                        size="small"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(currentPage - 1)}
                      >
                        <IonIcon icon={chevronBackOutline} />
                      </IonButton>
                    </div>

                    {/* Page indicator */}
                    <div className="flex items-center">
                      <span className="text-sm text-gray-600 hidden sm:block">
                        {t('common.page', 'Page')} {currentPage} {t('common.of', 'of')} {totalPages}
                      </span>
                      <span className="text-sm text-gray-600 sm:hidden">
                        {currentPage}/{totalPages}
                      </span>
                    </div>

                    {/* Desktop navigation - full text */}
                    <div className="hidden sm:flex items-center space-x-2">
                      <IonButton
                        fill="clear"
                        size="small"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(currentPage + 1)}
                        className="min-w-[60px]"
                      >
                        {t('common.next', 'Next')}
                        <IonIcon icon={chevronForwardOutline} slot="end" />
                      </IonButton>
                      <IonButton
                        fill="clear"
                        size="small"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(totalPages)}
                        className="min-w-[60px]"
                      >
                        {t('common.last', 'Last')}
                        <IonIcon icon={playForwardOutline} slot="end" />
                      </IonButton>
                    </div>

                    {/* Mobile navigation - icons only */}
                    <div className="flex sm:hidden items-center space-x-1">
                      <IonButton
                        fill="clear"
                        size="small"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(currentPage + 1)}
                      >
                        <IonIcon icon={chevronForwardOutline} />
                      </IonButton>
                      <IonButton
                        fill="clear"
                        size="small"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(totalPages)}
                      >
                        <IonIcon icon={playForwardOutline} />
                      </IonButton>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : groups.length > 0 ? (
            <div className="text-center py-8 ion-padding-horizontal">
              <IonIcon icon={searchOutline} className="text-4xl text-gray-400 mb-4" />
              <p className="text-gray-500 mb-4">{t('groups.noResults', 'No groups match your search criteria')}</p>
              <IonButton 
                fill="outline" 
                size="small"
                onClick={() => {
                  setSearchQuery('')
                  setMemberCountFilter('all')
                  setCurrentPage(1)
                }}
              >
                {t('common.clearFilters', 'Clear Filters')}
              </IonButton>
            </div>
          ) : (
            <div className="text-center py-8 ion-padding-horizontal">
              <IonIcon icon={peopleOutline} className="text-4xl text-gray-400 mb-4 hidden sm:block" />
              <p className="text-gray-500 mb-4">{t('groups.noGroups', 'No groups yet')}</p>
              <IonButton 
                fill="outline" 
                size="small"
                onClick={openCreateGroupModal}
              >
                <IonIcon icon={addOutline} />
                <span className="hidden sm:inline ml-2">{t('groups.createFirstGroup', 'Create Your First Group')}</span>
                <span className="sm:hidden ml-2">{t('groups.createGroup', 'Create Group')}</span>
              </IonButton>
            </div>
          )}
        </IonCardContent>
      </IonCard>

      {/* Create Group Modal */}
      <IonModal isOpen={showCreateGroupModal} onDidDismiss={() => setShowCreateGroupModal(false)}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>{t('groups.createGroup', 'Create Group')}</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setShowCreateGroupModal(false)}>
                <IonIcon icon={closeOutline} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <div className="space-y-4">
            <IonItem>
              <IonLabel position="stacked">
                {t('groups.groupName', 'Group Name')} *
              </IonLabel>
              <IonInput
                value={groupForm.name}
                placeholder={t('groups.groupNamePlaceholder', 'e.g., Leadership Team, Sales Team')}
                onIonInput={(e) => setGroupForm({...groupForm, name: e.detail.value!})}
              />
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">
                {t('groups.description', 'Description')}
              </IonLabel>
              <IonTextarea
                value={groupForm.description}
                placeholder={t('groups.descriptionPlaceholder', 'Optional description for this group')}
                rows={3}
                onIonInput={(e) => setGroupForm({...groupForm, description: e.detail.value!})}
              />
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">
                {t('groups.color', 'Group Color')}
              </IonLabel>
              <div className="flex flex-wrap gap-2 mt-2">
                {GROUP_COLORS.map((color) => (
                  <button
                    key={color}
                    className={`w-8 h-8 rounded-full border-2 ${
                      groupForm.color === color ? 'border-gray-800' : 'border-gray-300'
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => setGroupForm({...groupForm, color})}
                  />
                ))}
              </div>
            </IonItem>

            {availableCoachees.length > 0 && (
              <div>
                <IonLabel className="text-sm font-medium text-gray-700">
                  {t('groups.addMembers', 'Add Members')}
                </IonLabel>
                <IonNote className="block text-xs text-gray-500 mb-2">
                  {t('groups.addMembersNote', 'Select coachees to add to this group')}
                </IonNote>
                <IonList>
                  {availableCoachees.map((coachee) => (
                    <IonItem key={coachee.id}>
                      <IonCheckbox
                        checked={selectedCoachees.includes(coachee.id)}
                        onIonChange={(e) => {
                          if (e.detail.checked) {
                            setSelectedCoachees([...selectedCoachees, coachee.id])
                          } else {
                            setSelectedCoachees(selectedCoachees.filter(id => id !== coachee.id))
                          }
                        }}
                        slot="start"
                      />
                      <div slot="start">
                        <Avatar
                          user={{
                            id: coachee.id,
                            full_name: coachee.full_name,
                            email: coachee.email,
                            avatar_url: coachee.avatar_url
                          }}
                          currentUserId={user?.id}
                          relationship="coach"
                          size="medium"
                        />
                      </div>
                      <IonLabel>
                        <h3>{coachee.full_name}</h3>
                        <p>{coachee.email}</p>
                      </IonLabel>
                    </IonItem>
                  ))}
                </IonList>
              </div>
            )}

            <div className="mt-6">
              <IonButton
                expand="block"
                onClick={createGroup}
                disabled={isLoading || !groupForm.name.trim()}
              >
                {isLoading ? t('groups.creating', 'Creating...') : t('groups.createGroup', 'Create Group')}
              </IonButton>
            </div>
          </div>
        </IonContent>
      </IonModal>

      {/* Edit Group Modal */}
      <IonModal isOpen={showEditGroupModal} onDidDismiss={() => setShowEditGroupModal(false)}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>{t('groups.editGroup', 'Edit Group')}</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setShowEditGroupModal(false)}>
                <IonIcon icon={closeOutline} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <div className="space-y-4">
            <IonItem>
              <IonLabel position="stacked">
                {t('groups.groupName', 'Group Name')} *
              </IonLabel>
              <IonInput
                value={groupForm.name}
                placeholder={t('groups.groupNamePlaceholder', 'e.g., Leadership Team, Sales Team')}
                onIonInput={(e) => setGroupForm({...groupForm, name: e.detail.value!})}
              />
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">
                {t('groups.description', 'Description')}
              </IonLabel>
              <IonTextarea
                value={groupForm.description}
                placeholder={t('groups.descriptionPlaceholder', 'Optional description for this group')}
                rows={3}
                onIonInput={(e) => setGroupForm({...groupForm, description: e.detail.value!})}
              />
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">
                {t('groups.color', 'Group Color')}
              </IonLabel>
              <div className="flex flex-wrap gap-2 mt-2">
                {GROUP_COLORS.map((color) => (
                  <button
                    key={color}
                    className={`w-8 h-8 rounded-full border-2 ${
                      groupForm.color === color ? 'border-gray-800' : 'border-gray-300'
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => setGroupForm({...groupForm, color})}
                  />
                ))}
              </div>
            </IonItem>

            <div className="mt-6">
              <IonButton
                expand="block"
                onClick={updateGroup}
                disabled={isLoading || !groupForm.name.trim()}
              >
                {isLoading ? t('groups.updating', 'Updating...') : t('groups.updateGroup', 'Update Group')}
              </IonButton>
            </div>
          </div>
        </IonContent>
      </IonModal>

      {/* Group Details Modal */}
      <IonModal isOpen={showGroupDetailsModal} onDidDismiss={() => setShowGroupDetailsModal(false)}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>
              {t('groups.groupManagement', 'Group Management')}
            </IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setShowGroupDetailsModal(false)}>
                <IonIcon icon={closeOutline} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          {selectedGroup && (
            <>
              {/* Group Info Card */}
              <IonCard>
                <IonCardHeader>
                  <div className="flex items-center space-x-3">
                    <div 
                      className="w-6 h-6 rounded-full"
                      style={{ backgroundColor: selectedGroup.color }}
                    />
                    <IonCardTitle className="text-base sm:text-lg flex items-center">
                      <IonIcon icon={listOutline} className="mr-2 flex-shrink-0" />
                      {selectedGroup.name}
                    </IonCardTitle>
                  </div>
                </IonCardHeader>
                {selectedGroup.description && (
                  <IonCardContent>
                    <p className="text-gray-600">{selectedGroup.description}</p>
                  </IonCardContent>
                )}
              </IonCard>

              {/* Members Card */}
              <IonCard>
                <IonCardHeader>
                  <IonCardTitle className="flex items-center justify-between text-base sm:text-lg">
                    <div className="flex items-center">
                      <IonIcon icon={peopleOutline} className="mr-2 flex-shrink-0" />
                      {t('groups.members', 'Members')}
                    </div>
                    <div className="flex items-center space-x-1 flex-shrink-0" style={{marginRight: '-12px'}}>
                      <IonButton 
                        fill="clear" 
                        size="small"
                        onClick={() => openAddMembersModal(selectedGroup)}
                      >
                        <IonIcon icon={personAddOutline} />
                      </IonButton>
                    </div>
                  </IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  {/* Search and Filter Controls */}
                  {groupMembers.length > 0 && (
                    <div className="mb-4 relative">
                      <div className="sm:ml-7 sm:mr-12">
                        <IonSearchbar
                          value={memberSearchQuery}
                          onIonInput={(e: CustomEvent) => handleMemberSearchChange(e.detail.value!)}
                          placeholder={t('groups.searchMembers', 'Search members...')}
                          debounce={300}
                        />
                      </div>
                    </div>
                  )}

                  {filteredMembers.length > 0 ? (
                    <>
                      <div className="sm:ml-7">
                        <IonList>
                          {paginatedMembers.map((member) => (
                            <IonItemSliding key={member.id}>
                              <IonItem>
                                <div className="flex items-center w-full">
                                  <Avatar
                                    user={{
                                      id: member.coachee_id,
                                      full_name: member.coachee_name,
                                      email: member.coachee_email,
                                      profile_picture_url: member.coachee_avatar_url
                                    }}
                                    size="medium"
                                    className="mr-3 flex-shrink-0"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between">
                                      <h3 className="font-medium flex-1 min-w-0">{member.coachee_name}</h3>
                                      <div className="flex items-center space-x-1 ml-4 flex-shrink-0">
                                        <IonButton 
                                          fill="clear" 
                                          size="small"
                                          href={`mailto:${member.coachee_email}`}
                                        >
                                          <IonIcon icon={mailOutline} />
                                        </IonButton>
                                        <IonButton 
                                          fill="clear" 
                                          size="small"
                                          color="danger"
                                          onClick={() => confirmRemoveMember(member)}
                                        >
                                          <IonIcon icon={personRemoveOutline} />
                                        </IonButton>
                                      </div>
                                    </div>
                                    <p className="text-sm text-gray-600">{member.coachee_email}</p>
                                    <p className="text-xs text-gray-500">
                                      {t('groups.addedOn', 'Added on')} {new Date(member.added_at).toLocaleDateString()}
                                    </p>
                                  </div>
                                </div>
                              </IonItem>
                              <IonItemOptions side="end">
                                <IonItemOption 
                                  color="danger"
                                  onClick={() => confirmRemoveMember(member)}
                                >
                                  <IonIcon icon={personRemoveOutline} />
                                  {t('groups.remove', 'Remove')}
                                </IonItemOption>
                              </IonItemOptions>
                            </IonItemSliding>
                          ))}
                        </IonList>

                        {/* Pagination Controls */}
                        {memberTotalPages > 1 && (
                          <div className="flex items-center justify-between mt-4 px-4">
                            <IonButton
                              fill="clear"
                              size="small"
                              disabled={memberCurrentPage === 1}
                              onClick={() => setMemberCurrentPage(memberCurrentPage - 1)}
                            >
                              <IonIcon icon={chevronBackOutline} slot="start" />
                              {t('common.previous', 'Previous')}
                            </IonButton>
                            
                            <div className="flex items-center space-x-2">
                              <span className="text-sm text-gray-600">
                                {t('common.pageOf', 'Page {{current}} of {{total}}', { 
                                  current: memberCurrentPage, 
                                  total: memberTotalPages 
                                })}
                              </span>
                            </div>
                            
                            <IonButton
                              fill="clear"
                              size="small"
                              disabled={memberCurrentPage === memberTotalPages}
                              onClick={() => setMemberCurrentPage(memberCurrentPage + 1)}
                            >
                              {t('common.next', 'Next')}
                              <IonIcon icon={chevronForwardOutline} slot="end" />
                            </IonButton>
                          </div>
                        )}

                        {/* Results Summary */}
                        {groupMembers.length > memberItemsPerPage && (
                          <div className="text-center mt-4">
                            <p className="text-sm text-gray-600">
                              {t('groups.membersCount', 'Showing {{start}}-{{end}} of {{total}} members', {
                                start: memberStartIndex + 1,
                                end: Math.min(memberStartIndex + memberItemsPerPage, filteredMembers.length),
                                total: filteredMembers.length
                              })}
                            </p>
                          </div>
                        )}
                      </div>
                    </>
                  ) : groupMembers.length > 0 ? (
                    <div className="text-center py-8 sm:ml-7">
                      <IonIcon icon={searchOutline} className="text-4xl text-gray-400 mb-4" />
                      <p className="text-gray-500 mb-4">{t('groups.noMemberResults', 'No members match your search')}</p>
                      <IonButton 
                        fill="outline" 
                        size="small"
                        onClick={() => {
                          setMemberSearchQuery('')
                          setMemberCurrentPage(1)
                        }}
                      >
                        {t('common.clearFilters', 'Clear Filters')}
                      </IonButton>
                    </div>
                  ) : (
                    <div className="text-center py-8 sm:ml-7">
                      <IonIcon icon={peopleOutline} className="text-4xl text-gray-400 mb-4 hidden sm:block" />
                      <p className="text-gray-500 mb-4">
                        {t('groups.noMembers', 'No members in this group yet')}
                      </p>
                      <IonButton 
                        fill="outline" 
                        size="small"
                        onClick={() => openAddMembersModal(selectedGroup)}
                      >
                        <IonIcon icon={personAddOutline} />
                        <span className="hidden sm:inline ml-2">{t('groups.addMembers', 'Add Members')}</span>
                        <span className="sm:hidden ml-2">{t('groups.add', 'Add')}</span>
                      </IonButton>
                    </div>
                  )}
                </IonCardContent>
              </IonCard>

              {/* Group Actions Card */}
              <IonCard>
                <IonCardHeader>
                  <IonCardTitle className="text-base sm:text-lg flex items-center">
                    <IonIcon icon={settingsOutline} className="mr-2 flex-shrink-0" />
                    {t('groups.actions', 'Actions')}
                  </IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <div className="space-y-3">
                    <IonButton 
                      expand="block"
                      fill="outline"
                      onClick={() => {
                        setShowGroupDetailsModal(false)
                        openEditGroupModal(selectedGroup)
                      }}
                    >
                      <IonIcon icon={pencilOutline} slot="start" />
                      {t('groups.editGroup', 'Edit Group')}
                    </IonButton>
                    
                    <IonButton 
                      expand="block"
                      fill="outline"
                      color="danger"
                      onClick={() => {
                        setGroupToDelete(selectedGroup.id)
                        setShowDeleteAlert(true)
                        setShowGroupDetailsModal(false)
                      }}
                    >
                      <IonIcon icon={trashOutline} slot="start" />
                      {t('groups.deleteGroup', 'Delete Group')}
                    </IonButton>
                  </div>
                </IonCardContent>
              </IonCard>
            </>
          )}
        </IonContent>
      </IonModal>

      {/* Add Members Modal */}
      <IonModal isOpen={showAddMembersModal} onDidDismiss={() => setShowAddMembersModal(false)}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>{t('groups.addMembers', 'Add Members')}</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setShowAddMembersModal(false)}>
                <IonIcon icon={closeOutline} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <IonCard>
            <IonCardHeader>
              <IonCardTitle className="flex items-center text-base sm:text-lg">
                <IonIcon icon={personAddOutline} className="mr-2 flex-shrink-0" />
                {t('groups.addMembers', 'Add Members')}
              </IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              {getAvailableCoacheesForGroup().length > 0 ? (
                <>
                  <IonList>
                    {getAvailableCoacheesForGroup().map((coachee) => (
                      <IonItem key={coachee.id}>
                        <IonCheckbox
                          checked={selectedCoachees.includes(coachee.id)}
                          onIonChange={(e) => {
                            if (e.detail.checked) {
                              setSelectedCoachees([...selectedCoachees, coachee.id])
                            } else {
                              setSelectedCoachees(selectedCoachees.filter(id => id !== coachee.id))
                            }
                          }}
                          slot="start"
                        />
                        <IonAvatar slot="start">
                          {coachee.avatar_url ? (
                            <img src={coachee.avatar_url} alt={coachee.full_name} />
                          ) : (
                            <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                              <IonIcon icon={personOutline} className="text-gray-600" />
                            </div>
                          )}
                        </IonAvatar>
                        <IonLabel>
                          <h3 className="font-medium">{coachee.full_name}</h3>
                          <p className="text-sm text-gray-600">{coachee.email}</p>
                        </IonLabel>
                      </IonItem>
                    ))}
                  </IonList>

                  <div className="mt-4">
                    <IonButton
                      expand="block"
                      onClick={addMembersToGroup}
                      disabled={isLoading || selectedCoachees.length === 0}
                    >
                      {isLoading 
                        ? t('groups.addingMembers', 'Adding Members...') 
                        : t('groups.addSelectedMembers', 'Add {{count}} Member{{s}}', { 
                            count: selectedCoachees.length,
                            s: selectedCoachees.length !== 1 ? 's' : ''
                          })
                      }
                    </IonButton>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <IonIcon icon={peopleOutline} className="text-4xl text-gray-400 mb-4 hidden sm:block" />
                  <h3 className="text-lg font-medium text-gray-500 mb-2">
                    {t('groups.noAvailableMembers', 'No available members')}
                  </h3>
                  <p className="text-gray-400">
                    {t('groups.allMembersAdded', 'All your coachees are already in this group')}
                  </p>
                </div>
              )}
            </IonCardContent>
          </IonCard>
        </IonContent>
      </IonModal>



      {/* Remove Member Confirmation Alert */}
      <IonAlert
        isOpen={showRemoveMemberAlert}
        onDidDismiss={() => setShowRemoveMemberAlert(false)}
        header={t('groups.remove', 'Remove Member')}
        message={memberToRemove ? 
          t('groups.removeMemberConfirmation', 'Are you sure you want to remove {{name}} from this group?', { name: memberToRemove.coachee_name }) :
          ''
        }
        buttons={[
          {
            text: t('common.cancel', 'Cancel'),
            role: 'cancel'
          },
          {
            text: t('groups.remove', 'Remove'),
            role: 'destructive',
            handler: removeMemberFromGroup
          }
        ]}
      />

      {/* Delete Confirmation Alert */}
      <IonAlert
        isOpen={showDeleteAlert}
        onDidDismiss={() => setShowDeleteAlert(false)}
        header={t('groups.deleteGroup', 'Delete Group')}
        message={t('groups.deleteConfirmation', 'Are you sure you want to delete this group? This action cannot be undone.')}
        buttons={[
          {
            text: t('common.cancel', 'Cancel'),
            role: 'cancel'
          },
          {
            text: t('common.delete', 'Delete'),
            role: 'destructive',
            handler: deleteGroup
          }
        ]}
      />

      {/* Toast */}
      <IonToast
        isOpen={showToast}
        onDidDismiss={() => setShowToast(false)}
        message={toastMessage}
        duration={3000}
        position="top"
      />
    </>
  )
}

export default GroupManagement